import { toast } from 'sonner';

/**
 * Utility object for displaying different types of toast notifications.
 *
 * @namespace notifications
 * @property {Function} showSuccess - Displays a success notification.
 * @param {Object} options - Notification options.
 * @param {string} [options.message="Success"] - The main message to display.
 * @param {string} [options.description] - Additional description for the notification.
 * @param {number} [options.duration=5000] - Duration in milliseconds for which the notification is visible.
 * @param {React.ReactNode|null} [options.icon=null] - Optional icon to display.
 *
 * @property {Function} showError - Displays an error notification.
 * @param {Object} options - Notification options.
 * @param {string} [options.message="Error"] - The main message to display.
 * @param {string} [options.description] - Additional description for the notification.
 * @param {number} [options.duration=5000] - Duration in milliseconds for which the notification is visible.
 * @param {React.ReactNode|null} [options.icon=null] - Optional icon to display.
 *
 * @property {Function} showInfo - Displays an informational notification.
 * @param {Object} options - Notification options.
 * @param {string} [options.message="Info"] - The main message to display.
 * @param {string} [options.description] - Additional description for the notification.
 * @param {number} [options.duration=5000] - Duration in milliseconds for which the notification is visible.
 * @param {React.ReactNode|null} [options.icon=null] - Optional icon to display.
 *
 * @property {Function} showWithPromise - Displays a notification based on the state of a promise.
 * @param {Object} options - Notification options.
 * @param {Promise} options.promise - The promise to track.
 * @param {string} [options.message="Loading"] - The loading message to display.
 * @param {string} [options.description] - Description to display on success.
 * @param {number} [options.duration=5000] - Duration in milliseconds for which the notification is visible.
 * @param {React.ReactNode|null} [options.icon=null] - Optional icon to display.
 */
export const notifications = {
  showSuccess: ({ message="Success", description, duration = 5000, icon = null }) => {
    toast.success(message, {
      description,
      duration,
      icon,
    });
  },
  showError: ({ message="Error", description, duration = 5000, icon = null }) => {
    toast.error(message, {
      description,
      duration,
      icon,
    });
  },
  showInfo: ({ message="Info", description, duration = 5000, icon = null }) => {
    toast.info(message, {
      description,
      duration,
      icon,
    });
  },
  showWithPromise: ({ promise, message="Loading", description, duration = 5000, icon = null }) => {
    toast.promise(promise, {
      loading: message,
      success: () => {
        return description || 'Success';
      },
      error: 'Error',
    });
  }
};
